/**
 * Drawing for the journey map's city segments (MT-01): the surroundings of a street, the band
 * with the city's name banner where the journey enters it, and the silhouette street a city
 * shows until its art is in (a teaser always shows it). Every object goes through `own`, so
 * the scene can drop a whole segment when it scrolls far away.
 */
import type * as Phaser from 'phaser';
import type { Art } from '../game/art.js';
import type { Pt, StreetPlan } from '../game/shopLayout.js';
import { C, FONT, SILHOUETTE, css } from '../game/theme.js';
import { vp } from '../game/viewport.js';

export type Own = <T extends Phaser.GameObjects.GameObject>(o: T) => T;

/** One segment's place in the world (device px). */
export interface SegGeo {
  /** canvas width; the street column's left edge and width */
  W: number;
  x0: number;
  colW: number;
  /** street plan → world scale */
  k: number;
  /** world y of the segment's top; its street and band heights (the band is under the street) */
  top: number;
  streetH: number;
  bandH: number;
}

/** Band height in CSS px: room for the banner and its line, plus the clouds' edge. */
export const BAND_CSS = 150;

const SKY = 0x9fd6e6;
const HAZE = 0xf6e8c8;
const GRASS = 0x8cbd6f;
const CLOUD = 0xffffff;
const MIST = 0xeef7fa;

/** Sky over grass beside the column (wide screens), as the street art's own horizon. */
export function drawSurroundings(s: Phaser.Scene, own: Own, g: SegGeo, extraTop: number, extraBottom: number): void {
  const u = vp.u;
  const bg = own(s.add.graphics().setDepth(0));
  const horizon = g.top + g.streetH * 0.21;
  bg.fillGradientStyle(SKY, SKY, HAZE, HAZE, 1);
  bg.fillRect(0, g.top - extraTop, g.W, horizon - g.top + extraTop);
  bg.fillStyle(GRASS, 1);
  bg.fillRect(0, horizon, g.W, g.streetH * 0.79);
  if (extraBottom > 0) bg.fillRect(0, g.top + g.streetH + g.bandH, g.W, extraBottom);
  if (g.x0 > 0) {
    bg.fillStyle(0x000000, 0.06);
    bg.fillRect(g.x0 - 6 * u, g.top, 6 * u, g.streetH);
    bg.fillRect(g.x0 + g.colW, g.top, 6 * u, g.streetH);
  }
}

/**
 * The band under a street, where the journey enters the city: the home city's rests on grass;
 * the others float in sky, clouds over the seam with the street above (the city before this
 * one ends in its skyline, below).
 */
export function drawBand(s: Phaser.Scene, own: Own, g: SegGeo, home: boolean): void {
  const u = vp.u;
  const y0 = g.top + g.streetH;
  const bg = own(s.add.graphics().setDepth(2));
  if (home) {
    bg.fillStyle(GRASS, 1);
    bg.fillRect(0, y0, g.W, g.bandH);
    return;
  }
  bg.fillGradientStyle(MIST, MIST, SKY, SKY, 1);
  bg.fillRect(0, y0, g.W, g.bandH);
  // puffy cloud edge over the seam (fixed pattern: every segment looks the same, no randomness)
  const step = 44 * u;
  for (let x = -step / 2, i = 0; x < g.W + step; x += step, i++) {
    const r = (i % 3 === 0 ? 34 : i % 3 === 1 ? 26 : 30) * u;
    bg.fillStyle(CLOUD, 0.92);
    bg.fillCircle(x, y0 + 4 * u + (i % 2) * 6 * u, r);
  }
  bg.fillStyle(CLOUD, 0.55);
  for (let x = 0, i = 0; x < g.W + step; x += step * 1.5, i++) bg.fillCircle(x, y0 + g.bandH - 6 * u, (i % 2 ? 18 : 24) * u);
}

export interface BannerSpec {
  name: string;
  /** the line under the name: the level range, or what opens the city */
  sub: string;
  /** a teaser: a lock in front of the line */
  locked: boolean;
}

/** The city's name banner, centred in its band; returns the banner's box (taps, test hooks). */
export function drawBanner(s: Phaser.Scene, own: Own, art: Art, g: SegGeo, b: BannerSpec): { x: number; y: number; w: number; h: number } {
  const u = vp.u;
  const cx = g.x0 + g.colW / 2;
  const cy = g.top + g.streetH + 58 * u;
  const h = 58 * u;
  const name = own(s.add.text(cx, cy, b.name, { fontFamily: FONT, fontSize: `${Math.round(30 * u)}px`, fontStyle: '700', color: css(C.ink) }).setOrigin(0.5).setDepth(34));
  const maxW = Math.min(g.colW, g.W) - 72 * u;
  if (name.width > maxW - 40 * u) name.setScale((maxW - 40 * u) / name.width);
  const w = Math.min(maxW, Math.max(180 * u, name.displayWidth + 56 * u));
  const rib = own(s.add.graphics().setDepth(33));
  // ribbon tails behind the plate
  const tail = 30 * u;
  rib.fillStyle(C.kraftDark, 1);
  for (const dir of [-1, 1]) {
    const x = cx + dir * (w / 2 - 6 * u);
    const xo = x + dir * tail;
    rib.fillTriangle(x, cy - h * 0.3, xo, cy - h * 0.3, x, cy + h * 0.45);
    rib.fillTriangle(xo, cy - h * 0.3, xo - dir * 10 * u, cy + h * 0.08, xo, cy + h * 0.45);
    rib.fillTriangle(x, cy + h * 0.45, xo, cy - h * 0.3, xo, cy + h * 0.45);
  }
  rib.fillStyle(C.ink, 0.35);
  rib.fillRoundedRect(cx - w / 2, cy - h / 2 + 4 * u, w, h, 18 * u);
  rib.fillStyle(C.cream, 1);
  rib.fillRoundedRect(cx - w / 2, cy - h / 2, w, h, 18 * u);
  rib.lineStyle(3 * u, C.ink, 1);
  rib.strokeRoundedRect(cx - w / 2, cy - h / 2, w, h, 18 * u);
  // the line under it: a small dark pill, with a lock for a teaser
  const ly = cy + h / 2 + 22 * u;
  const line = own(s.add.text(cx, ly, b.sub, { fontFamily: FONT, fontSize: `${Math.round(15 * u)}px`, fontStyle: '700', color: '#ffffff' }).setOrigin(0.5).setDepth(35));
  const lockW = b.locked ? 20 * u : 0;
  if (line.width > maxW - 28 * u - lockW) line.setScale((maxW - 28 * u - lockW) / line.width);
  const lw = line.displayWidth + 28 * u + lockW;
  line.setX(cx + lockW / 2);
  const pill = own(s.add.graphics().setDepth(34));
  pill.fillStyle(C.ink, 0.85);
  pill.fillRoundedRect(cx - lw / 2, ly - 14 * u, lw, 28 * u, 14 * u);
  if (b.locked) {
    const f = art.get('ui_lock');
    own(s.add.image(cx - lw / 2 + 14 * u + 8 * u, ly, f.texture, f.frame).setDisplaySize(18 * u, 18 * u).setDepth(35));
  }
  return { x: cx, y: (cy - h / 2 + ly + 14 * u) / 2, w: w + tail * 2, h: ly + 14 * u - (cy - h / 2) };
}

/**
 * A street in silhouette: muted sky and skyline, the road, the five shop plots. Teasers show
 * it for good (with a lock); a playable city shows it until its atlases arrive. Its look is
 * theme SILHOUETTE, which the next-city card (features/citycards) draws as well.
 */
export function drawSilhouette(s: Phaser.Scene, own: Own, art: Art, g: SegGeo, plan: StreetPlan, lock: boolean): void {
  const u = vp.u;
  const k = g.k;
  const at = (p: Pt) => ({ x: g.x0 + p[0] * k, y: g.top + p[1] * k });
  const gr = own(s.add.graphics().setDepth(1));
  const vista = g.streetH * 0.26;
  const S = SILHOUETTE;
  gr.fillGradientStyle(S.sky[0], S.sky[0], S.sky[1], S.sky[1], 1);
  gr.fillRect(g.x0, g.top, g.colW, vista);
  gr.fillStyle(S.ground, 1);
  gr.fillRect(g.x0, g.top + vista, g.colW, g.streetH - vista);
  // a skyline of blocks along the vista's foot (fixed widths and heights: no randomness)
  gr.fillStyle(S.skyline, 1);
  const { blocks, tall } = S;
  let x = g.x0;
  for (let i = 0; x < g.x0 + g.colW; i++) {
    const bw = blocks[i % blocks.length]! * g.colW;
    const bh = tall[i % tall.length]! * vista * 0.7;
    gr.fillRect(x, g.top + vista - bh, Math.min(bw, g.x0 + g.colW - x), bh);
    x += bw;
  }
  gr.fillStyle(S.skylineFoot, 1);
  gr.fillRect(g.x0, g.top + vista - 10 * u, g.colW, 10 * u);
  // the road
  gr.lineStyle(62 * k, S.road, 1);
  gr.strokePoints(plan.path.map(at), false, false);
  gr.fillStyle(S.road, 1);
  const start = at(plan.path[0]!);
  gr.fillEllipse(start.x, start.y - 40 * k, 360 * k, 150 * k);
  // shop plots
  gr.fillStyle(S.plot, 0.9);
  for (const p of Object.values(plan.plots)) {
    const fw = plan.facade.w * p.scale * k;
    const fh = plan.facade.h * p.scale * k;
    const c = at([p.x, p.y]);
    gr.fillRoundedRect(c.x - fw / 2, c.y - fh * 0.82, fw, fh * 0.82, 10 * u);
    gr.fillTriangle(c.x - fw * 0.56, c.y - fh * 0.8, c.x + fw * 0.56, c.y - fh * 0.8, c.x, c.y - fh);
  }
  if (!lock) return;
  const cx = g.x0 + g.colW / 2;
  const cy = g.top + g.streetH * 0.58;
  gr.fillStyle(0xffffff, 0.75);
  gr.fillCircle(cx, cy, 52 * u);
  gr.lineStyle(4 * u, C.ink, 0.8);
  gr.strokeCircle(cx, cy, 52 * u);
  const f = art.get('ui_lock');
  own(s.add.image(cx, cy, f.texture, f.frame).setDisplaySize(64 * u, 64 * u).setDepth(2));
}
